/**
 * A RESTING CARD IS DRAWN AT ITS OWN SIZE — the rest view.
 *
 * A card that enlarges IN PLACE (the hand's 3.8x hover zoom) keeps its art
 * raster at the enlarged size all the time (`rasterReserveScale`), so the
 * first enlarged frame is already sharp and never waits for a raster. At rest
 * that same canvas is shown at a quarter of its size, and nothing reduces it
 * well: an element canvas is minified on every frame with no mipmaps, in
 * Chromium, Gecko and WebKit alike — the contract's §2b aliasing ("oversampling
 * makes it worse, not better"). Measured 2026-10-04 on the battle hand at
 * 1920x1080 DPR 1 (the owner's desktop: "unhovered cards in hand are blurrier
 * than they used to be"; on a DPR 2 Mac "they were perfect"): the resting art
 * carried 2.5-12x the high-frequency energy of a proper reduction of the very
 * same pixels — grain and jagged edges, not detail.
 *
 * In Chromium it also softened the TEXT. A canvas is a composited layer, so
 * the title, cost and description painted over it were lifted into layers of
 * their own, rasterised flat and rotated by the compositor through the fan's
 * ±6/12 degrees: bilinear resampling of 7px letters on four of five fan cards.
 *
 * So while such a card is AT REST it shows this module's rest view instead: an
 * IMAGE holding the reserve reduced ONCE, with a high-quality filter, to
 * exactly the art's used size x DPR — the same crop, because it is the whole
 * reserve canvas — while the reserve canvas itself is hidden and paints
 * nothing. An image, not a canvas: Chromium composites every 2D canvas,
 * CPU-backed (`willReadFrequently`) ones included (measured: a `Canvas` layer
 * per card and the text lifted over it again), while an `<img>` is painted
 * with its card. Enlarged, carried or leaning, the reserve is shown again,
 * already sharp at its size, so a hover still never waits for a raster.
 *
 * Showing and hiding is pure CSS on the card's state (game-board.css); this
 * module only keeps the rest pixels truthful:
 *   - every repaint of the reserve (a new node, its size, request, tier or
 *     source) is seen by a MutationObserver whose callback runs before the
 *     next frame. A rest view of ANOTHER picture is withdrawn in that same
 *     task (`data-card-art-rest` no longer `ready`: the reserve shows) and
 *     redrawn; the
 *     same picture at a new size, DPR or tier keeps its current rest view up
 *     — scaled by the browser for those frames, as the pipeline's own resize
 *     refit is — until its replacement is ready;
 *   - a replacement is decoded first, and then the node already on screen
 *     takes its pixels in that one step (see `present`): no frame shows it
 *     half-loaded, and no new element appears where a card already had one.
 * A card says where its rest view stands in `data-card-art-rest`: `pending`
 * while one is owed and being drawn (the reserve shows meanwhile), `ready`
 * once it is the art on screen. Gates that must not read layout — the match
 * loading screen asks every frame whether the board is whole — read that.
 * The reduction is whichever this engine does well, measured (see
 * reductionEngine): `createImageBitmap(..., resizeQuality: 'high')` in Gecko,
 * an explicit area average in Chromium and WebKit.
 *
 * EXACT SIZE IS EXACT ONLY SQUARE TO THE SCREEN. Owner, 5 October 2026, on the
 * first build of this module: "it's sharper but it greatly increased aliasing.
 * only card art is not aliased, that's blurry, perhaps even more blurry than
 * before". Both were true, and neither showed on a CPU-rendering test machine
 * or against a reduction of the same pixels: they show against a supersampled
 * render of the same hand on screen (16 samples per pixel), on a GPU.
 *   - THE ART of a card turned in the fan is resampled by the browser
 *     whatever it is given, bilinearly: an exact-size raster kept 0.31-0.44
 *     of the detail. So is the art of ANY card on a screen whose CSS pixel is
 *     not a whole number of device pixels (a 125% desktop: 0.61-0.65 on the
 *     card square to the screen). Such a card keeps a raster a little FINER
 *     than the screen and is read straight from its own pixels (see
 *     RESAMPLED).
 *   - THE FRAME is one 736x1312 picture drawn at about 120 px on every paint.
 *     Painted with the fan on a GPU its thin lines broke into dashes (1.7-2.4x
 *     the detail, i.e. aliasing). A resting card shows its frame reduced ONCE
 *     to its own size instead, exactly as it shows its art (see framePlan).
 * Contract: tests/e2e-playwright/hand-rest-sharpness*.spec.js,
 * tests/unit/ui/cardRestView.test.js.
 *
 * The rest view never carries `.card-art-paint`: a card face has exactly one
 * paint node, and `copyCanvasPaints` (drag clone, ghost, effect snapshots)
 * copies only that one. A clone keeps its copied rest view hidden (CSS shows
 * it only on a resting hand card); its `data:` source stays valid there, so
 * nothing ever fails to load in a clone.
 */

export const REST_VIEW_CLASS = 'card-art-rest';
export const FRAME_REST_CLASS = 'card-frame-rest';
const REST_VIEW_STATE = 'cardArtRest';
const FRAME_REST_STATE = 'cardFrameRest';
const PENDING = 'pending';
const READY = 'ready';

/* A reserve this close to the rest size is already an exact raster. */
const EXACT_ENOUGH = 1.05;

/* A CARD THE BROWSER RESAMPLES — one turned in the fan, or any card where a
   CSS pixel is not a whole number of device pixels (see resampledAtRest): how
   much finer than the screen its rest raster is kept, and whether the rest
   view must say it is to be read from its own pixels (`data-rest-sampling`,
   cards.css). Measured on screen against a supersampled render of the same
   hand, 1920x976 at DPR 1, 5 October 2026 — Laplacian energy (1.00 is the
   render's; lower is blur, higher is aliasing) and RMS luma error, on the
   cards at -12 and -6 degrees:
     Chromium (RTX 3090)   exact 0.32 / 12.9-15.4;  1.4x 0.78-0.81;
               1.5x 0.88-0.89 / 6.9-8.7;  1.6x 0.99-1.01 / 6.9-8.6;  1.75x 1.14.
               Left to `auto`, anything past 1.41x is read from its half-size
               mip level instead (1.5x: 0.67): hence the word. CPU raster reads
               the same numbers with or without it.
     Gecko     exact 0.44 / 12.8-14.2;  1.5x 0.99-1.06 / 11.8-12.1;  1.75x
               1.25-1.39. `auto` first reduces ANY image to its drawn size
               (every size read 0.34-0.38): hence the word there too. Its own
               reduction is sharper than an area average, so it needs less.
     WebKit    exact 0.38 / 11.8-15.3;  1.5x 0.84-0.88 / 9.4-13.0;  2x 1.30-
               1.34. It reads an image bilinearly as it is; the keyword that
               asks Chromium for that asks WebKit for nearest-neighbour, so it
               is never said there.
   A card square to the screen at a whole device pixel ratio is the one case
   Chromium and Gecko draw an exact raster as it is (Chromium there: exact
   1.00 / 0.3, 1.6x 0.90 / 3.9), and it keeps one. WebKit does not even then
   (`always`): it places what is inside a transformed box wherever it falls,
   and a hand card always carries a transform (its pose). Its square card
   with an exact raster read 1.01 where it happened to land on whole pixels
   (1400x640) and 0.44-0.48 where it did not (1920x1080; 1470x832 at DPR 2). */
const RESAMPLED = {
    chromium: { finer: 1.6, sampling: 'linear', always: false },
    gecko: { finer: 1.5, sampling: 'linear', always: false },
    webkit: { finer: 1.6, sampling: '', always: true },
};
const POSE = '--card-base-transform';
const DEGREES = { deg: 1, grad: 0.9, rad: 180 / Math.PI, turn: 360 };

/* While a window is being dragged to a new size every frame reports another
   one, and the art pipeline repaints every reserve on each. A rest view that
   is up stays up through that, scaled by the browser, and is redrawn once
   the size has held this long (see onWindowResize). */
const RESIZE_SETTLE_MS = 120;
/* Reduced frames kept: a few frame pictures at the last few sizes. */
const FRAME_RASTERS_KEPT = 12;

const ATTRIBUTES = [
    'class',
    'width',
    'height',
    'data-card-art-source',
    'data-card-art-raster-request',
    'data-card-art-raster-tier',
    'data-card-art-raster-key',
];

const observedContainers = new WeakSet();
const containers = new Set();
const renderGenerations = new WeakMap();
const inflightSignatures = new WeakMap();
/* Pixels whose rest view could not be made (see render): not owed again. */
const failedSignatures = new WeakMap();
const readyWaiters = new WeakMap();
/* Reduced frames by signature (picture and size): one serves every card. */
const frameRasters = new Map();
const failedFrames = new Set();
const inflightFrames = new WeakMap();
let resizeObserver = null;
let dprQuery = null;
let resizingUntil = 0;
let resizeTimer = null;

function usedLength(value) {
    return typeof value === 'string' && value.endsWith('px') ? Number.parseFloat(value) : NaN;
}

function wellOf(card) {
    return card?.querySelector?.(':scope > .card-image') || null;
}

function reserveOf(card) {
    return wellOf(card)?.querySelector(':scope > canvas.card-art-paint') || null;
}

function restViewOf(card) {
    return wellOf(card)?.querySelector(`:scope > .${REST_VIEW_CLASS}`) || null;
}

function frameRestOf(card) {
    return card?.querySelector?.(`:scope > .${FRAME_REST_CLASS}`) || null;
}

function resizing() {
    return Date.now() < resizingUntil;
}

/** A card the rest view may show on: nothing is enlarging, carrying or leaning it. */
function isAtRest(card) {
    if (!card?.isConnected) return false;
    if (['hover-active', 'card-tilt-active', 'dragging', 'safari-hand-zoom-hover']
        .some((name) => card.classList.contains(name))) return false;
    return !(Number.parseFloat(card.style?.zoom) > 1);
}

function withdraw(card) {
    if (card?.dataset && REST_VIEW_STATE in card.dataset) delete card.dataset[REST_VIEW_STATE];
}

/** Everyone waiting on this card's rest view hears it is settled (shown or not owed). */
function settleWaiters(card) {
    const waiters = readyWaiters.get(card);
    if (!waiters) return;
    readyWaiters.delete(card);
    waiters.forEach((resolve) => resolve());
}

/* A card has two rest views, its art's and its frame's: whoever waits on the
   card hears when neither is still on its way. Asked at the END of whatever
   changed one of them — a sync brings both up to date before it asks, so a
   frame that is not owed never answers for art that is about to be. */
function settle(card) {
    if (card?.dataset?.[REST_VIEW_STATE] === PENDING || card?.dataset?.[FRAME_REST_STATE] === PENDING) return;
    settleWaiters(card);
}

function retire(card) {
    withdraw(card);
    renderGenerations.delete(card);
    inflightSignatures.delete(card);
    restViewOf(card)?.remove();
}

/**
 * Does this card rest turned? Read from the pose it is styled to hold (the
 * fan's rule for its seat), never from its transform: that is mid-flight for
 * the whole of a glide, and a raster is made for where the card comes to rest.
 */
function restsTurned(card) {
    const pose = card.ownerDocument.defaultView.getComputedStyle(card).getPropertyValue(POSE) || '';
    const turn = /rotate\(\s*(-?(?:\d+\.?\d*|\.\d+))(deg|grad|rad|turn)\s*\)/.exec(pose);
    if (!turn) return false;
    const off = Math.abs(Number(turn[1]) * DEGREES[turn[2]]) % 90;
    return Math.min(off, 90 - off) > 0.05;
}

/**
 * Will the browser resample this card's rest raster, whatever its size? It
 * does when the card is turned — and when a CSS pixel is not a whole number
 * of device pixels: a hand card always carries a transform (its pose), what
 * is inside a transformed box is placed on whole CSS pixels, and at 1.25
 * those fall between device pixels. Measured at 1536x864 DPR 1.25 (a full HD
 * desktop at 125%), Chromium on GPU and CPU alike: the card square to the
 * screen read 0.65 of its supersampled render with an exact raster and
 * 0.92-0.95 with the finer one. And in an engine that never places it on
 * whole pixels (see RESAMPLED), always.
 */
function resampledAtRest(card, dpr, engine) {
    return engine.always || Math.abs(dpr - Math.round(dpr)) > 0.01 || restsTurned(card);
}

/**
 * What the rest view of `card` must be right now, or null when there is
 * nothing to reduce: no settled reserve canvas, or one that is already exact.
 * Exactly used size x DPR for a card the browser draws as it is; finer, and
 * read from its own pixels, for one it resamples (see RESAMPLED).
 */
export function restPlan(card) {
    const paint = reserveOf(card);
    if (!paint || !paint.isConnected || paint.classList.contains('card-art-loading')) return null;
    if (!(paint.width > 0 && paint.height > 0)) return null;
    const view = paint.ownerDocument.defaultView;
    const style = view.getComputedStyle(paint);
    const width = usedLength(style.width);
    const height = usedLength(style.height);
    if (!(width > 0 && height > 0)) return null;
    const dpr = view.devicePixelRatio || 1;
    const exactWidth = Math.max(1, Math.round(width * dpr));
    const exactHeight = Math.max(1, Math.round(height * dpr));
    if (paint.width <= exactWidth * EXACT_ENOUGH && paint.height <= exactHeight * EXACT_ENOUGH) return null;
    // Finer for a card the browser resamples, and never finer than its reserve.
    const engine = RESAMPLED[reductionEngine()];
    const resampled = resampledAtRest(card, dpr, engine) ? engine : null;
    const finer = resampled
        ? Math.max(1, Math.min(resampled.finer, paint.width / exactWidth, paint.height / exactHeight))
        : 1;
    const targetWidth = finer > 1 ? Math.min(paint.width, Math.round(width * dpr * finer)) : exactWidth;
    const targetHeight = finer > 1 ? Math.min(paint.height, Math.round(height * dpr * finer)) : exactHeight;
    const sampling = finer > 1 ? resampled.sampling : '';
    const source = paint.dataset.cardArtSource || '';
    return {
        paint,
        targetWidth,
        targetHeight,
        // How the rest view is to be read (cards.css): '' is the browser's own way.
        sampling,
        // Which picture: a rest view of any other is never shown for a frame.
        source,
        // Exactly what it was reduced from and to: a change is redrawn.
        signature: [
            source,
            `${targetWidth}x${targetHeight}`,
            paint.dataset.cardArtRasterRequest || '',
            paint.dataset.cardArtRasterTier || '',
            `${paint.width}x${paint.height}`,
            sampling,
        ].join('|'),
    };
}

/* Which reduction this engine does WELL — measured 2026-10-04 against an
   independent Lanczos3 reduction of the same reserve pixels (Laplacian
   variance ratio / luma correlation; >1.6 is aliasing):
     Chromium  createImageBitmap 'high' 0.58-0.72 / 0.99 (canvas-2D the same)
     Gecko     createImageBitmap 'high' 1.00 / 1.000; canvas-2D 'high' 2.6-3.6
     WebKit    canvas-2D and createImageBitmap alike 2.9-6.8; halving 2.1-3.7
   So Gecko resamples with createImageBitmap, and WebKit averages the
   reserve's pixels itself (an area filter: no engine filter to trust).
   Chromium's 0.58-0.72 passed that day as "not aliased"; it is also soft,
   and the owner saw it ("blurry, perhaps even more blurry than before").
   On screen, against a supersampled render of the same card square to the
   screen (5 October 2026, RTX 3090 and CPU raster alike): createImageBitmap
   'high' 0.66-0.72 of the detail, error 6.2-8.9; the area average 0.92-0.93,
   error 3.2. So Chromium averages too. Engine identity, as the sampler's own
   bitmap gate does it, never a probe. */
function reductionEngine() {
    /** @type {any} userAgentData is not in every DOM typing. */
    const nav = window.navigator || {};
    const brands = nav.userAgentData?.brands;
    const ua = String(nav.userAgent || '');
    if (Array.isArray(brands) ? brands.some((entry) => entry?.brand === 'Chromium')
        : (/Chrome\//.test(ua) && !/Firefox|FxiOS/.test(ua))) return 'chromium';
    // Firefox on iOS is WebKit.
    if (/Firefox\//.test(ua) && !/FxiOS/.test(ua)) return 'gecko';
    return 'webkit';
}

/**
 * Box-filter reduction of RGBA pixels: every output pixel is the coverage-
 * weighted mean of the source pixels under it. Separable; exact for any ratio.
 *
 * The taps of each output pixel are CONTIGUOUS source pixels, so they are kept
 * as a first index, a count and a run of weights in typed arrays, and walked
 * by index. The first version kept arrays of [index, weight] pairs and walked
 * them with destructuring: in WebKit, the one engine that reduces this way,
 * that cost 316 ms for a single hand card at DPR 1 and 394 ms at DPR 2
 * (Playwright WebKit, 2026-10-05; 13 ms for the same code in Firefox), and a
 * hand's cards are reduced in one task. Same pixels, byte for byte
 * (tests/unit/ui/cardRestView.test.js keeps the first version as reference).
 */
export function areaAverage(source, sourceWidth, sourceHeight, target, targetWidth, targetHeight) {
    const tapsFor = (sourceLength, targetLength) => {
        const scale = sourceLength / targetLength;
        const most = Math.ceil(scale) + 1;
        const first = new Int32Array(targetLength);
        const count = new Int32Array(targetLength);
        const weights = new Float64Array(targetLength * most);
        for (let index = 0; index < targetLength; index += 1) {
            const start = index * scale;
            const end = start + scale;
            const from = Math.floor(start);
            const to = Math.min(sourceLength, Math.ceil(end));
            let kept = 0;
            for (let at = from; at < to; at += 1) {
                const weight = Math.min(at + 1, end) - Math.max(at, start);
                if (weight > 0) {
                    weights[index * most + kept] = weight / scale;
                    kept += 1;
                }
            }
            first[index] = from;
            count[index] = kept;
        }
        return { first, count, weights, most };
    };
    const columns = tapsFor(sourceWidth, targetWidth);
    const rows = tapsFor(sourceHeight, targetHeight);
    const stride = targetWidth * 4;
    const horizontal = new Float32Array(stride * sourceHeight);
    for (let y = 0; y < sourceHeight; y += 1) {
        const sourceRow = y * sourceWidth * 4;
        const outRow = y * stride;
        for (let x = 0; x < targetWidth; x += 1) {
            const taps = columns.count[x];
            const weightAt = x * columns.most;
            let from = sourceRow + columns.first[x] * 4;
            let r = 0;
            let g = 0;
            let b = 0;
            let a = 0;
            // Summed as the 32-bit floats the row buffer holds, tap by tap:
            // the arithmetic of the first version, so the pixels are its own.
            for (let tap = 0; tap < taps; tap += 1) {
                const weight = columns.weights[weightAt + tap];
                r = Math.fround(r + source[from] * weight);
                g = Math.fround(g + source[from + 1] * weight);
                b = Math.fround(b + source[from + 2] * weight);
                a = Math.fround(a + source[from + 3] * weight);
                from += 4;
            }
            const out = outRow + x * 4;
            horizontal[out] = r;
            horizontal[out + 1] = g;
            horizontal[out + 2] = b;
            horizontal[out + 3] = a;
        }
    }
    for (let y = 0; y < targetHeight; y += 1) {
        const taps = rows.count[y];
        const weightAt = y * rows.most;
        const firstRow = rows.first[y] * stride;
        const outRow = y * stride;
        for (let x = 0; x < targetWidth; x += 1) {
            let from = firstRow + x * 4;
            let r = 0;
            let g = 0;
            let b = 0;
            let a = 0;
            for (let tap = 0; tap < taps; tap += 1) {
                const weight = rows.weights[weightAt + tap];
                r += horizontal[from] * weight;
                g += horizontal[from + 1] * weight;
                b += horizontal[from + 2] * weight;
                a += horizontal[from + 3] * weight;
                from += stride;
            }
            const out = outRow + x * 4;
            target[out] = r;
            target[out + 1] = g;
            target[out + 2] = b;
            target[out + 3] = a;
        }
    }
    return target;
}

/* The reserve's own pixels. WebKit reads card canvases back already (the
   art pipeline's sampler does). Chromium's reserve lives on the GPU and must
   stay there — a canvas that is read back repeatedly is moved to the CPU, and
   this one is what an enlarged card shows — so a copy kept in memory is read
   and the reserve is only ever drawn from. */
function reservePixels(paint, engine) {
    if (engine === 'webkit') return paint.getContext('2d').getImageData(0, 0, paint.width, paint.height);
    const copy = paint.ownerDocument.createElement('canvas');
    copy.width = paint.width;
    copy.height = paint.height;
    const context = copy.getContext('2d', { willReadFrequently: true });
    if (!context) throw new Error('no 2D context');
    context.drawImage(paint, 0, 0);
    const pixels = context.getImageData(0, 0, paint.width, paint.height);
    copy.width = 1;
    copy.height = 1;
    return pixels;
}

/** The reserve reduced once, high quality, into a detached canvas. */
async function reduce(plan) {
    const { paint, targetWidth, targetHeight } = plan;
    const canvas = paint.ownerDocument.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    // Encoded as soon as it is drawn (encode): kept in memory, not on the GPU.
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) throw new Error('no 2D context');
    const engine = reductionEngine();
    /* CHROMIUM PAINTS A RESERVE TWICE: a canvas-2D pass at once, then its
       bitmap reduction into the same canvas a moment later, which it stamps
       with its tier when it lands (cards.js; 150-280 ms apart on the opening
       hand, usually on a card still held for its deal). An area average of
       each cost 12-36 ms of main thread per card, twice. The pass that is
       about to be painted over gets the browser's own reduction, made off the
       main thread; the average is spent once, on the pixels that stay. */
    const passing = engine === 'chromium' && !paint.dataset.cardArtRasterTier;
    if ((engine === 'gecko' || passing) && typeof createImageBitmap === 'function') {
        const bitmap = await createImageBitmap(paint, {
            resizeWidth: targetWidth,
            resizeHeight: targetHeight,
            resizeQuality: 'high',
        });
        try {
            context.drawImage(bitmap, 0, 0);
        } finally {
            bitmap.close?.();
        }
        return canvas;
    }
    const pixels = reservePixels(paint, engine);
    const reduced = context.createImageData(targetWidth, targetHeight);
    areaAverage(pixels.data, paint.width, paint.height, reduced.data, targetWidth, targetHeight);
    context.putImageData(reduced, 0, 0);
    return canvas;
}

/* A data: URL, not a blob: one — a clone of the card copies the attribute, and
   a revoked blob would fail to load there. Made straight from the canvas, in
   this task. The off-thread route (toBlob, then a FileReader) looked kinder
   to the main thread and was not: the deal waits for the rest view, and each
   trip back through the event loop was slowest exactly when a match is busy.
   Measured on the opening draw, per 115x137 rest view (phone seat 932x300 at
   DPR 3, a loaded machine, 5 October 2026):
     Chromium  toBlob 43-1125 ms — it encodes in IDLE time and forces a start
               only after a second; the draw left 420 ms after main's;
     WebKit    FileReader 107-1114 ms — a blob read through the network
               process, queued with the match's own art downloads; the drawn
               card was seen up to a second after main's (and WebKit encodes
               on the main thread even through toBlob: indicators.js);
     Gecko     both under 30 ms.
   A rest view is a few tens of thousands of pixels: encoding it here took
   1-3 ms in Chromium and 5-31 ms in WebKit in the same runs (under 1 ms in
   Gecko, whose seat is DPR 1: 38x46). The rest view then follows its art by
   20-90 ms where it took 40-1170, and the opening draw leaves 50-60 ms after
   main's in Chromium and Gecko and level with it in WebKit (six interleaved
   pairs per engine). Nothing slower belongs between a reserve and its rest
   view: no idle task, no worker round trip, no blob. */
function encode(canvas) {
    return canvas.toDataURL('image/png');
}

/* RECONCILE, NEVER REBUILD. A card that already shows a rest view keeps that
   node and gives it the new pixels; a new <img> in its place is a new element
   on screen, and the snappy meter counted one pop-in per hand card on every
   window resize (board-resize, 4 October 2026). The decoded twin has put this
   picture in the document's list of available images, so the node is complete
   with it in this same task — measured in Chrome, Firefox and WebKit: given
   the same URL with no decoded twin, each shows an incomplete image for up to
   a frame. An engine that does not take it at once gets the twin itself, so
   the picture on screen is never an incomplete one. */
/* How a rest view's pixels are to be read (see RESAMPLED, and cards.css). */
function readAs(image, plan) {
    if (plan.sampling) image.dataset.restSampling = plan.sampling;
    else delete image.dataset.restSampling;
}

function present(card, paint, twin, plan, url) {
    let shown = restViewOf(card);
    if (shown) {
        shown.src = url;
        if (shown.complete && shown.naturalWidth > 0) {
            shown.dataset.restSignature = plan.signature;
            if (plan.source) shown.dataset.cardArtSource = plan.source;
            else delete shown.dataset.cardArtSource;
            // With its pixels, never before or after them.
            readAs(shown, plan);
        } else {
            shown.replaceWith(twin);
            shown = twin;
        }
    } else {
        shown = twin;
    }
    if (shown.previousElementSibling !== paint) paint.after(shown);
}

function render(card, plan) {
    // A repaint writes several attributes in one task: one render per picture.
    if (inflightSignatures.get(card) === plan.signature) return;
    const generation = (renderGenerations.get(card) || 0) + 1;
    renderGenerations.set(card, generation);
    inflightSignatures.set(card, plan.signature);
    const current = () => renderGenerations.get(card) === generation && card.isConnected;
    (async () => {
        const canvas = await reduce(plan);
        if (!current()) return;
        const url = encode(canvas);
        canvas.width = 1;
        canvas.height = 1;
        if (!url) throw new Error('no image');
        const image = card.ownerDocument.createElement('img');
        image.className = REST_VIEW_CLASS;
        image.alt = '';
        image.setAttribute('aria-hidden', 'true');
        // Decoded before it is shown (decode() below); a node that later takes
        // new pixels in place paints them in that same frame.
        image.decoding = 'sync';
        image.draggable = false;
        image.dataset.restSignature = plan.signature;
        // Which picture this raster is, as the reserve names it: the snappy
        // recorder's manifest follows a generated raster by that name.
        if (plan.source) image.dataset.cardArtSource = plan.source;
        readAs(image, plan);
        image.src = url;
        await image.decode();
        // Still this card's picture and pixels, at this size?
        if (!current() || restPlan(card)?.signature !== plan.signature) return;
        const paint = reserveOf(card);
        if (!paint) return;
        present(card, paint, image, plan, url);
        failedSignatures.delete(card);
        card.dataset[REST_VIEW_STATE] = READY;
        settle(card);
    })().catch(() => {
        /* No reduction or no decode: the reserve keeps showing, exactly as
           before. And these pixels are not owed a rest view again: a reserve
           the page may not read back (cards.js draws one in its degraded
           paths, where "a tainted canvas is safe" because it is only
           displayed) fails every time, and a card that went on answering
           `pending` made the deal wait out its whole cap on every draw. */
        if (renderGenerations.get(card) !== generation) return;
        failedSignatures.set(card, plan.signature);
        retire(card);
        settle(card);
    }).finally(() => {
        if (renderGenerations.get(card) !== generation) return;
        inflightSignatures.delete(card);
        if (card.dataset?.[REST_VIEW_STATE] !== PENDING) return;
        /* Drawn for a reserve that moved on meanwhile (a card enlarged or
           repainted while its rest view was being made): bring it up to
           date, and never leave a card saying one is on its way when
           nothing is — a gate would wait out its whole cap on it. */
        syncCardRestView(card);
        if (!inflightSignatures.has(card) && card.dataset?.[REST_VIEW_STATE] === PENDING) {
            withdraw(card);
            settle(card);
        }
    });
}

/* ---------------------------------------------------------------------------
   THE FRAME AT REST. A card's frame is the `::before` layer of the card
   itself: one 736x1312 picture fitted to the card (`background-size: cover`,
   centred — cards.css), so about a 6x reduction on every paint. Painted with
   the fan on a GPU, Chromium reads it from the nearest mip level and through
   the fan's turn, and the frame's thin lines break into dashes; measured on
   screen against a supersampled render of the same hand (RTX 3090, 1920x976,
   DPR 1, 5 October 2026 — Laplacian energy / RMS luma error over the title
   and plate of the cards at -12, -6 and 0 degrees):
     Chromium GPU   2.42 / 21.5, 1.76 / 19.9, 1.67 / 27.7
        reduced once  0.85 / 8.9,  0.90 / 8.6,  0.91 / 11.0
     Chromium CPU   0.91 / 9.0,  1.01 / 8.2,  1.00 / 9.6   (it reduces well:
        why no CPU-rendering test machine ever showed this)
     WebKit         2.31 / 22.2, 1.65 / 23.3, 1.60 / 30.9
        reduced once  0.94 / 14.1, 0.88 / 14.4, 0.91 / 11.6
     Gecko          0.71 / 10.0, 0.77 / 11.3, 0.83 / 12.5 either way: it
        reduces an image to its drawn size with a good filter before the fan
        turns it. It is left alone.
   So a resting card shows the frame reduced ONCE by an area average to
   exactly its own size x DPR, as an image right over its art, and the
   stylesheet's picture paints nothing meanwhile (game-board.css). The layer
   itself stays: it draws the card's edge line. One reduction serves every
   card of that picture and size, so after the first the next card has its
   frame in the task that asks. The same promises as the art's rest view:
   decoded before it is shown, the node on screen takes new pixels in place,
   another picture is never shown for a frame, and a card says where it stands
   in `data-card-frame-rest`.
   --------------------------------------------------------------------------- */

/* A single plain picture: anything else is left to the browser. */
function pictureOf(backgroundImage) {
    const match = /^url\((['"]?)([^'"()]+)\1\)$/.exec(String(backgroundImage || '').trim());
    return match ? match[2] : '';
}

/**
 * What the resting frame of `card` must be right now, or null when it is left
 * to the browser: Gecko (it reduces a frame well), a card with no frame
 * picture or no box, or a picture the stylesheet fits any other way than
 * covering the card from its centre.
 */
export function framePlan(card) {
    if (!card?.isConnected || reductionEngine() === 'gecko') return null;
    const view = card.ownerDocument.defaultView;
    const layer = view.getComputedStyle(card, '::before');
    const source = pictureOf(layer.backgroundImage);
    if (!source) return null;
    /* While this card shows its reduced frame the stylesheet gives the layer's
       picture no size, so that it paints nothing (game-board.css); the fit it
       hides is the one this frame was made for. */
    const fit = String(layer.backgroundSize || '');
    const unsized = /^0(px)?( 0(px)?)?$/.test(fit) && card.dataset?.[FRAME_REST_STATE] === READY;
    if (fit !== 'cover' && !unsized) return null;
    if (!/^(50% 50%|center( center)?)$/.test(String(layer.backgroundPosition || ''))) return null;
    const width = usedLength(layer.width);
    const height = usedLength(layer.height);
    if (!(width > 0 && height > 0)) return null;
    const dpr = view.devicePixelRatio || 1;
    const targetWidth = Math.max(1, Math.round(width * dpr));
    const targetHeight = Math.max(1, Math.round(height * dpr));
    return {
        source,
        width,
        height,
        targetWidth,
        targetHeight,
        signature: `${source}|${targetWidth}x${targetHeight}`,
    };
}

/**
 * The area average of a picture that has transparent pixels (a frame has a
 * window, and round corners). A canvas reads "nothing" back as transparent
 * BLACK: averaged channel by channel that black bleeds into every edge, a dark
 * fringe round the art window. Each pixel counts by its alpha instead, as a
 * browser's own filtering does. `source` is consumed.
 */
export function averageFrame(source, sourceWidth, sourceHeight, targetWidth, targetHeight) {
    for (let at = 0; at < source.length; at += 4) {
        const alpha = source[at + 3];
        if (alpha === 255) continue;
        source[at] = (source[at] * alpha) / 255;
        source[at + 1] = (source[at + 1] * alpha) / 255;
        source[at + 2] = (source[at + 2] * alpha) / 255;
    }
    const weighted = areaAverage(
        source, sourceWidth, sourceHeight,
        new Float32Array(targetWidth * targetHeight * 4), targetWidth, targetHeight,
    );
    const target = new Uint8ClampedArray(weighted.length);
    for (let at = 0; at < weighted.length; at += 4) {
        const alpha = weighted[at + 3];
        if (!(alpha > 0)) continue;
        const scale = 255 / alpha;
        target[at] = weighted[at] * scale;
        target[at + 1] = weighted[at + 1] * scale;
        target[at + 2] = weighted[at + 2] * scale;
        target[at + 3] = alpha;
    }
    return target;
}

function frameImage(card, plan, url) {
    const image = card.ownerDocument.createElement('img');
    image.className = FRAME_REST_CLASS;
    image.alt = '';
    image.setAttribute('aria-hidden', 'true');
    // As the art's rest view: decoded before it is shown, painted whole.
    image.decoding = 'sync';
    image.draggable = false;
    image.dataset.frameSignature = plan.signature;
    /* Which picture this raster is: the snappy recorder follows a generated
       raster by the file it names, and reads it from this attribute (one the
       art's own `data-card-art-source` would claim as card art). */
    image.dataset.originalSrc = plan.source;
    image.src = url;
    return image;
}

/* The picture, cut as the stylesheet fits it (it covers the card from its
   centre) and reduced to the card's own pixels. The same picture the
   stylesheet names, loaded the same way, so it is the browser's own copy. */
async function reduceFrame(card, plan) {
    const document = card.ownerDocument;
    const picture = document.createElement('img');
    picture.decoding = 'async';
    picture.src = plan.source;
    await picture.decode();
    const { naturalWidth, naturalHeight } = picture;
    if (!(naturalWidth > 0 && naturalHeight > 0)) throw new Error('no picture');
    const fit = Math.max(plan.width / naturalWidth, plan.height / naturalHeight);
    const seenWidth = Math.min(naturalWidth, plan.width / fit);
    const seenHeight = Math.min(naturalHeight, plan.height / fit);
    /* Chromium halves the picture as it cuts it, where the frame is to be
       less than half its size anyway: its canvas draws a halving from the
       picture's own mip level, which IS an area average, and reading back
       and averaging a quarter of the pixels took 9-12 ms where the whole
       picture took 32-38 (1920x1080, two frame pictures back to back at
       match start). WebKit's halving aliases (see reductionEngine): there
       the whole picture is averaged. */
    const halved = reductionEngine() === 'chromium'
        && plan.targetWidth * 2 <= seenWidth && plan.targetHeight * 2 <= seenHeight ? 2 : 1;
    const cut = document.createElement('canvas');
    cut.width = Math.max(1, Math.round(seenWidth / halved));
    cut.height = Math.max(1, Math.round(seenHeight / halved));
    const cutContext = cut.getContext('2d', { willReadFrequently: true });
    const canvas = document.createElement('canvas');
    canvas.width = plan.targetWidth;
    canvas.height = plan.targetHeight;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!cutContext || !context) throw new Error('no 2D context');
    cutContext.imageSmoothingEnabled = true;
    cutContext.imageSmoothingQuality = 'high';
    cutContext.drawImage(
        picture,
        (naturalWidth - seenWidth) / 2, (naturalHeight - seenHeight) / 2, seenWidth, seenHeight,
        0, 0, cut.width, cut.height,
    );
    const pixels = cutContext.getImageData(0, 0, cut.width, cut.height);
    const reduced = context.createImageData(plan.targetWidth, plan.targetHeight);
    reduced.data.set(averageFrame(pixels.data, cut.width, cut.height, plan.targetWidth, plan.targetHeight));
    context.putImageData(reduced, 0, 0);
    const url = encode(canvas);
    cut.width = 1;
    cut.height = 1;
    canvas.width = 1;
    canvas.height = 1;
    if (!url) throw new Error('no image');
    /* Kept decoded for as long as the reduction is kept: it holds this URL in
       the document's list of available images, so every node given it — the
       next card's, or one on screen taking new pixels — is complete at once. */
    const twin = frameImage(card, plan, url);
    await twin.decode();
    return { url, twin };
}

/* The reduction of this picture at this size: made once, shared by every
   card that asks, in this task when it already exists. */
function frameRaster(card, plan) {
    let raster = frameRasters.get(plan.signature);
    if (raster) {
        // Most recently used last: the oldest are the ones let go.
        frameRasters.delete(plan.signature);
        frameRasters.set(plan.signature, raster);
        return raster;
    }
    raster = { made: null, making: null };
    raster.making = reduceFrame(card, plan).then((made) => {
        raster.made = made;
        return made;
    }, (error) => {
        /* No picture, or one the page may not read back: the stylesheet's
           frame keeps showing, exactly as before, and this picture at this
           size is not owed again — to this card or the next. */
        frameRasters.delete(plan.signature);
        if (failedFrames.size > 64) failedFrames.clear();
        failedFrames.add(plan.signature);
        throw error;
    });
    frameRasters.set(plan.signature, raster);
    for (const signature of frameRasters.keys()) {
        if (frameRasters.size <= FRAME_RASTERS_KEPT) break;
        frameRasters.delete(signature);
    }
    return raster;
}

function withdrawFrame(card) {
    if (card?.dataset && FRAME_REST_STATE in card.dataset) delete card.dataset[FRAME_REST_STATE];
}

function retireFrame(card) {
    withdrawFrame(card);
    inflightFrames.delete(card);
    frameRestOf(card)?.remove();
}

/* RECONCILE, NEVER REBUILD (see present): the node on screen takes the new
   pixels; a decoded node goes in only where there is none, or where this
   engine did not take them at once. */
function seatFrame(card, plan, node, url) {
    const well = wellOf(card);
    if (!well) return;
    let shown = frameRestOf(card);
    if (shown && shown !== node) {
        shown.src = url;
        if (shown.complete && shown.naturalWidth > 0) {
            shown.dataset.frameSignature = plan.signature;
            shown.dataset.originalSrc = plan.source;
        } else {
            shown.replaceWith(node);
            shown = node;
        }
    } else {
        shown = node;
    }
    // Right over the art: above the well, under the frame layer's edge line.
    if (shown.previousElementSibling !== well) well.after(shown);
    card.dataset[FRAME_REST_STATE] = READY;
}

function placeFrame(card, plan) {
    if (inflightFrames.get(card) === plan.signature) return;
    const raster = frameRaster(card, plan);
    inflightFrames.set(card, plan.signature);
    const wanted = () => inflightFrames.get(card) === plan.signature && card.isConnected
        && framePlan(card)?.signature === plan.signature;
    const seat = (made) => {
        const node = frameImage(card, plan, made.url);
        if (node.complete && node.naturalWidth > 0) {
            inflightFrames.delete(card);
            seatFrame(card, plan, node, made.url);
            return null;
        }
        // This engine wants it decoded again: never an incomplete picture.
        return node.decode().then(() => {
            if (!wanted()) return;
            inflightFrames.delete(card);
            seatFrame(card, plan, node, made.url);
        });
    };
    const made = raster.made ? seat(raster.made) : raster.making.then((ready) => (wanted() ? seat(ready) : null));
    // In place already: the sync that asked tells whoever waits.
    if (!made) return;
    made.catch(() => {
        // Not owed again (see frameRaster): nothing waits on what will not come.
        failedFrames.add(plan.signature);
        if (inflightFrames.get(card) === plan.signature) retireFrame(card);
    }).finally(() => settleFrameAttempt(card, plan));
}

/* Never leave a card saying a frame is on its way when nothing is (a card
   enlarged or resized while its frame was being made): bring it up to date,
   and take the mark back if that drew nothing — a gate would wait out its
   whole cap on it. Then whoever waits on the card hears. */
function settleFrameAttempt(card, plan) {
    if (inflightFrames.get(card) === plan.signature) inflightFrames.delete(card);
    if (!inflightFrames.has(card) && card.dataset?.[FRAME_REST_STATE] === PENDING) {
        if (card.isConnected && isAtRest(card)) syncFrameRest(card);
        if (!inflightFrames.has(card) && card.dataset?.[FRAME_REST_STATE] === PENDING) withdrawFrame(card);
    }
    settle(card);
}

/** Brings one card's resting frame up to date with its frame layer. */
function syncFrameRest(card) {
    const plan = framePlan(card);
    if (!plan || failedFrames.has(plan.signature)) {
        retireFrame(card);
        return;
    }
    const shown = frameRestOf(card);
    if (shown?.dataset.frameSignature === plan.signature) {
        if (card.dataset[FRAME_REST_STATE] !== READY) card.dataset[FRAME_REST_STATE] = READY;
        return;
    }
    // Another picture: never shown for a frame, and one is owed. The same
    // picture at another size stays up until its replacement is in place —
    // and through a window resize, until that is over.
    if (!shown || shown.dataset.originalSrc !== plan.source) {
        card.dataset[FRAME_REST_STATE] = PENDING;
    } else if (resizing()) {
        return;
    }
    placeFrame(card, plan);
}

/* A rebuilt card takes its predecessor's reduced frame as it takes its art's
   rest view. It is not in the page yet, so its own frame layer cannot be read
   here: its first sync runs before it is painted, and withdraws a frame of
   another picture in that same task. */
function adoptFrameRest(previousCard, nextCard) {
    const frame = frameRestOf(previousCard);
    const well = wellOf(nextCard);
    if (!frame || !well || previousCard.dataset?.[FRAME_REST_STATE] !== READY) return;
    frameRestOf(nextCard)?.remove();
    well.after(frame);
    nextCard.dataset[FRAME_REST_STATE] = READY;
}

/** Brings one card's rest views, its frame's and its art's, up to date. */
export function syncCardRestView(card) {
    if (!card?.isConnected || !wellOf(card) || !isAtRest(card)) return;
    syncFrameRest(card);
    syncArtRest(card);
    settle(card);
}

function syncArtRest(card) {
    const plan = restPlan(card);
    if (!plan || failedSignatures.get(card) === plan.signature) {
        retire(card);
        return;
    }
    const rest = restViewOf(card);
    if (rest?.dataset.restSignature === plan.signature) {
        if (card.dataset[REST_VIEW_STATE] !== READY) card.dataset[REST_VIEW_STATE] = READY;
        return;
    }
    // Another picture: never shown for a frame, and one is owed. The same
    // picture at another size, DPR, tier or pose stays up until its
    // replacement is ready — and through a window resize, until that is over.
    if (!rest || !rest.dataset.restSignature?.startsWith(`${plan.source}|`)) {
        card.dataset[REST_VIEW_STATE] = PENDING;
    } else if (resizing()) {
        return;
    }
    render(card, plan);
}

/**
 * Is this card still owed a rest view — a settled, reducible reserve with no
 * rest view of its picture on screen yet? The hand's arrival gates (the
 * deal's art wait, the cohort hold) wait on this exactly as they wait on the
 * art itself, so a card is first seen with its rest view, never with its
 * reserve minified for the frames the reduction takes. Pixels that already
 * failed to make one are not owed: nothing waits on what will not come.
 * The frame's rest view is owed the same way, and waited on the same way.
 */
export function restViewPending(card) {
    if (!card?.isConnected || !containers.has(card.parentElement)) return false;
    return artPending(card) || framePending(card);
}

function artPending(card) {
    if (card.dataset?.[REST_VIEW_STATE] === READY) return false;
    const plan = restPlan(card);
    return Boolean(plan) && failedSignatures.get(card) !== plan.signature;
}

function framePending(card) {
    if (card.dataset?.[FRAME_REST_STATE] === READY) return false;
    const plan = framePlan(card);
    return Boolean(plan) && !failedFrames.has(plan.signature);
}

/** Resolves once the card's rest view is shown, or is not owed, or after `capMs`. */
export function whenRestViewSettled(card, capMs = 2000) {
    if (!restViewPending(card)) return Promise.resolve();
    return new Promise((resolve) => {
        const timer = setTimeout(resolve, capMs);
        const waiters = readyWaiters.get(card) || [];
        waiters.push(() => {
            clearTimeout(timer);
            resolve();
        });
        readyWaiters.set(card, waiters);
        syncCardRestView(card);
    });
}

/**
 * A card rebuilt from a previous node of the same face (the hand's
 * reconciliation) takes that node's decoded rest view along with its paint,
 * so it is never shown with its reserve minified while its own is drawn. The
 * next sync redraws it if the new reserve's pixels differ; the same picture
 * stays up meanwhile.
 */
export function adoptRestView(previousCard, nextCard) {
    adoptFrameRest(previousCard, nextCard);
    const rest = restViewOf(previousCard);
    const paint = reserveOf(nextCard);
    if (!rest || !paint || previousCard.dataset?.[REST_VIEW_STATE] !== READY) return false;
    if ((paint.dataset.cardArtSource || '') !== (reserveOf(previousCard)?.dataset.cardArtSource || '')) return false;
    restViewOf(nextCard)?.remove();
    paint.after(rest);
    nextCard.dataset[REST_VIEW_STATE] = READY;
    return true;
}

function syncContainer(container) {
    for (const card of container.querySelectorAll(':scope > .card')) {
        observeWell(card);
        syncCardRestView(card);
    }
}

function observeWell(card) {
    const well = wellOf(card);
    if (well && resizeObserver) resizeObserver.observe(well);
}

function isRestView(node) {
    return node?.nodeType === 1
        && (node.classList.contains(REST_VIEW_CLASS) || node.classList.contains(FRAME_REST_CLASS));
}

function onMutations(records) {
    const touched = new Set();
    for (const record of records) {
        /* The hand gained or lost a card: the fan seats the others again,
           and a seat is a pose (`:nth-child`, game-board.css) that touches
           none of their attributes. A card that turned, or came square,
           is owed the raster of its new pose. */
        if (record.type === 'childList' && containers.has(record.target)) {
            for (const card of record.target.querySelectorAll(':scope > .card')) touched.add(card);
            continue;
        }
        const element = record.target.nodeType === 1 ? record.target : record.target.parentElement;
        // The rest view's own arrival and departure are not news.
        if (isRestView(element)) continue;
        if (record.type === 'childList'
            && [...record.addedNodes, ...record.removedNodes].every(isRestView)) continue;
        const card = element?.closest?.('.card');
        if (card) touched.add(card);
        for (const node of record.addedNodes || []) {
            if (node.nodeType === 1 && node.matches('.card')) touched.add(node);
        }
    }
    for (const card of touched) {
        if (!containers.has(card.parentElement)) continue;
        observeWell(card);
        syncCardRestView(card);
    }
}

function onResize(entries) {
    for (const entry of entries) {
        const card = entry.target.closest?.('.card');
        if (card && containers.has(card.parentElement)) syncCardRestView(card);
    }
}

/* A WINDOW BEING RESIZED IS NOT REDRAWN FOR ON EVERY FRAME. The art pipeline
   repaints every reserve on each frame of a resize, and each repaint asks for
   its rest view again. An area average is main-thread work (5-14 ms a card),
   so answering each would hold the resize itself back. The rest views that
   are up stay up, scaled by the browser as the reserves themselves are for
   those frames, and are redrawn once when the size has held still. A card
   with nothing up yet is never deferred: an arrival may be waiting on it. */
function onWindowResize() {
    resizingUntil = Date.now() + RESIZE_SETTLE_MS;
    if (resizeTimer !== null) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
        resizeTimer = null;
        resizingUntil = 0;
        for (const container of containers) {
            if (container.isConnected) syncContainer(container);
        }
    }, RESIZE_SETTLE_MS);
}

function watchDevicePixelRatio() {
    if (typeof window.matchMedia !== 'function') return;
    dprQuery?.removeEventListener?.('change', onDprChange);
    dprQuery = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
    dprQuery.addEventListener?.('change', onDprChange);
}

function onDprChange() {
    watchDevicePixelRatio();
    for (const container of containers) {
        if (container.isConnected) syncContainer(container);
    }
}

/**
 * Keep rest views for the cards directly inside `container` (the hand row).
 * Idempotent; the observers live as long as the page.
 */
export function observeRestViews(container) {
    if (!container) return;
    if (observedContainers.has(container)) {
        syncContainer(container);
        return;
    }
    observedContainers.add(container);
    containers.add(container);
    if (!resizeObserver && typeof ResizeObserver === 'function') resizeObserver = new ResizeObserver(onResize);
    if (!dprQuery) watchDevicePixelRatio();
    if (containers.size === 1) window.addEventListener('resize', onWindowResize, { passive: true });
    if (typeof MutationObserver === 'function') {
        new MutationObserver(onMutations).observe(container, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: ATTRIBUTES,
        });
    }
    syncContainer(container);
}

export const CardRestView = {
    observe: observeRestViews,
    sync: syncCardRestView,
    plan: restPlan,
    pending: restViewPending,
    whenSettled: whenRestViewSettled,
    adopt: adoptRestView,
    REST_VIEW_CLASS,
    FRAME_REST_CLASS,
};
